import {useLandTicker} from '#/hooks/query/use-land-ticker';
import {CoinDisplayPreferences, useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {roundDecimals} from '#/utils/math-utils';
import {GraphRange, TradingViewBarEntry} from '@shareland/shareland-common';

export type LandGraphData = {
  oraclePrice: number | null;
  marketPrice: number | null;
  volume: number | null;
  date: number;
};

const tradingBarViewEntryToGraphData = (
  tradingBarViewEntry: TradingViewBarEntry,
  displayPref: CoinDisplayPreferences,
): LandGraphData => {
  const {oracleClose, marketClose, volume, time} = tradingBarViewEntry;

  const floorWithDisplayPref = (value: number) =>
    roundDecimals(displayPref.usdcToNumericDisplayAmount(value) / decimalsMultiplier, 2);

  return {
    marketPrice: floorWithDisplayPref(marketClose),
    oraclePrice: floorWithDisplayPref(oracleClose),
    volume: floorWithDisplayPref(volume),
    date: new Date(time).getTime(),
  };
};

export const useLandGraphData = (graphRange: GraphRange, tokenSymbol: string) => {
  const displayPref = useCoinDisplayPreferences();

  const {data = [], ...rest} = useLandTicker(graphRange, tokenSymbol);

  return {
    data: data.map(entry => tradingBarViewEntryToGraphData(entry, displayPref)),
    ...rest,
  };
};

export const getLandGraphDataMinMaxValues = (
  graphData: LandGraphData[],
  activeKeys: (keyof Omit<LandGraphData, 'date'>)[],
) =>
  graphData.reduce(
    (acc, value) => {
      if (value.marketPrice === null || value.oraclePrice === null || value.volume === null) {
        return acc;
      }

      if (!activeKeys.includes('oraclePrice')) {
        return {
          min: Math.min(acc.min, value.marketPrice),
          max: Math.max(acc.max, value.marketPrice),
          maxVolume: Math.max(acc.maxVolume, value.volume),
        };
      }
      if (!activeKeys.includes('marketPrice')) {
        return {
          min: Math.min(acc.min, value.oraclePrice),
          max: Math.max(acc.max, value.oraclePrice),
          maxVolume: Math.max(acc.maxVolume, value.volume),
        };
      }

      return {
        min: Math.min(acc.min, value.marketPrice, value.oraclePrice),
        max: Math.max(acc.max, value.marketPrice, value.oraclePrice),
        maxVolume: Math.max(acc.maxVolume, value.volume),
      };
    },
    {min: Number.MAX_VALUE, max: 0, maxVolume: 0},
  );
